import { useCallback, useEffect, useRef, useState } from 'react';

import { getCollectionTileLayouts } from '../services/cardLayouts';
import { isTokenValid } from '../utils';

/**
 * The arrangement that applies to each card of a collection — one request per
 * collection, cascaded on the server (card -> collection -> user).
 *
 * `cardsVersion` (CardDefinitionsContext.version) refetches beside the card
 * cache: a card that arrived after the page loaded brings its layout with it.
 *
 * Returns `{ layouts, refresh }`. `layouts` is a cardId -> layout map and is
 * `{}` both before the fetch lands and when nothing is arranged, so the grid
 * renders its automatic pick immediately and swaps to an arrangement when one
 * arrives — a layout is a refinement, never a reason to hold a list back.
 */
export default function useTileLayouts(collectionId, cardsVersion = 0) {
	const [layouts, setLayouts] = useState({});
	// A fetch in flight must not overwrite a newer one's result.
	const requestRef = useRef(0);

	const refresh = useCallback(() => {
		if (!collectionId || !isTokenValid()) {
			setLayouts({});
			return;
		}
		const token = ++requestRef.current;
		getCollectionTileLayouts(collectionId)
			.then(next => { if (token === requestRef.current) setLayouts(next); })
			// The grid works without arrangements; a failure is the automatic pick.
			.catch(() => { if (token === requestRef.current) setLayouts({}); });
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [collectionId, cardsVersion]);

	useEffect(refresh, [refresh]);

	return { layouts, refresh };
}
